"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, CheckCircle2, XCircle, Package, FileText, Wallet, ArrowRight, BookOpen, Eye, Send, Play } from "lucide-react";
import { SimpleTabs, type SimpleTab } from "../SimpleTabs";
import { DataTable, type Column } from "../DataTable";
import { CSVExportButton } from "../PrintExport";
import { useERP, formatBDT, formatNum, type ID } from "@/lib/erp/store";
import { useToast } from "@/hooks/use-toast";

export interface ProcurementModuleProps {
  onNavigate: (pageId: string) => void;
}

export function ProcurementModule({ onNavigate }: ProcurementModuleProps) {
  const erp = useERP();
  const [tab, setTab] = React.useState("po");

  const tabs: SimpleTab[] = [
    { value: "requisitions", label: "Requisitions" },
    { value: "po", label: "Purchase Orders", count: erp.purchaseOrders.length },
    { value: "grn", label: "Goods Receipt", count: erp.goodsReceipts.length },
    { value: "si", label: "Supplier Invoices" },
    { value: "payments", label: "Payments" },
    { value: "suppliers", label: "Suppliers", count: erp.suppliers.length },
    { value: "guide", label: "Procure-to-Pay Guide" },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold sm:text-2xl">Procurement</h1>
          <p className="text-muted-foreground mt-1 text-sm">Manage purchase orders, goods receipt, supplier invoices and payments.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <CSVExportButton columns={[{ key: "poNumber", label: "PO #" }, { key: "supplier", label: "Supplier" }, { key: "total", label: "Total" }]} rows={erp.purchaseOrders.map(p => ({ poNumber: p.poNumber, supplier: erp.getSupplier(p.supplierId)?.name, total: p.total }))} filename="purchase-orders.csv" />
          <Button size="sm" onClick={() => onNavigate("po-new")}><Plus className="size-4" /> New PO</Button>
        </div>
      </div>

      <SimpleTabs value={tab} onValueChange={setTab} tabs={tabs} />

      {tab === "requisitions" && <RequisitionsTab />}
      {tab === "po" && <POTab onNavigate={onNavigate} />}
      {tab === "grn" && <GRNTab onNavigate={onNavigate} />}
      {tab === "si" && <SupplierInvoiceTab />}
      {tab === "payments" && <PaymentsTab />}
      {tab === "suppliers" && <SuppliersTab onNavigate={onNavigate} />}
      {tab === "guide" && <PTPGuide />}
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-muted-foreground text-xs font-medium uppercase tracking-wide">{label}</CardTitle></CardHeader>
      <CardContent><div className={`text-2xl font-semibold ${tone || ""}`}>{value}</div></CardContent>
    </Card>
  );
}

function RequisitionsTab() {
  const erp = useERP();
  const rows = erp.purchaseOrders.filter(p => p.status === "Pending Approval").map(p => ({
    id: p.id, reqNo: `PR-${p.poNumber.split("-").pop()}`, poNumber: p.poNumber,
    supplier: erp.getSupplier(p.supplierId)?.name ?? "—", date: p.orderDate, total: p.total, status: p.status,
  }));
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Pending Requisitions" value={formatNum(rows.length)} />
        <StatCard label="Pending Value" value={formatBDT(rows.reduce((s, r) => s + r.total, 0))} tone="text-destructive" />
        <StatCard label="Suppliers" value={formatNum(new Set(rows.map(r => r.supplier)).size)} />
        <StatCard label="Avg Cycle Time" value="2.4 days" />
      </div>
      <Card>
        <CardHeader><CardTitle>Pending Requisitions</CardTitle></CardHeader>
        <CardContent>
          <DataTable columns={[
            { key: "reqNo", header: "PR #", sortable: true, render: r => <span className="font-mono text-xs">{r.reqNo}</span> },
            { key: "poNumber", header: "Linked PO" },
            { key: "supplier", header: "Supplier", sortable: true },
            { key: "date", header: "Date" },
            { key: "total", header: "Amount", align: "right", sortable: true, render: r => formatBDT(r.total) },
            { key: "status", header: "Status", render: r => <Badge variant="secondary">{r.status}</Badge> },
          ] as Column<typeof rows[number]>[]} data={rows} rowKey={r => r.id} searchKeys={["reqNo", "poNumber", "supplier"]} />
        </CardContent>
      </Card>
    </div>
  );
}

function POTab({ onNavigate }: { onNavigate: (pageId: string) => void }) {
  const erp = useERP();
  const rows = erp.purchaseOrders;
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total POs" value={formatNum(rows.length)} />
        <StatCard label="Pending Approval" value={formatNum(rows.filter(r => r.status === "Pending Approval").length)} tone="text-destructive" />
        <StatCard label="Received" value={formatNum(rows.filter(r => r.status === "Received").length)} tone="text-primary" />
        <StatCard label="Total Value" value={formatBDT(rows.reduce((s, r) => s + r.total, 0))} />
      </div>
      <Card>
        <CardHeader><CardTitle>Purchase Orders</CardTitle></CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { key: "poNumber", header: "PO #", sortable: true, render: r => <span className="font-mono text-xs text-primary">{r.poNumber}</span> },
              { key: "supplier", header: "Supplier", sortable: true, render: r => erp.getSupplier(r.supplierId)?.name ?? "—" },
              { key: "orderDate", header: "Order Date", sortable: true },
              { key: "expectedDelivery", header: "Expected" },
              { key: "total", header: "Total", align: "right", sortable: true, render: r => formatBDT(r.total) },
              { key: "status", header: "Status", render: r => <Badge variant={r.status === "Received" ? "default" : r.status === "Rejected" ? "destructive" : "secondary"}>{r.status}</Badge> },
              { key: "actions", header: "Actions", align: "right", render: r => (
                <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" className="size-7 p-0" onClick={() => onNavigate(`po-detail:${r.id}`)}><Eye className="size-3.5" /></Button>
                  {r.status === "Pending Approval" && <>
                    <Button size="sm" variant="ghost" className="size-7 p-0 text-emerald-600" onClick={() => erp.approvePO(r.id, "Manager")}><CheckCircle2 className="size-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="size-7 p-0 text-rose-600" onClick={() => erp.rejectPO(r.id, "Rejected")}><XCircle className="size-3.5" /></Button>
                  </>}
                  {(r.status === "Approved" || r.status === "Partially Received") && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onNavigate(`po-detail:${r.id}`)}><Package className="size-3 mr-1" />Receive</Button>
                  )}
                </div>
              )},
            ] as Column<typeof rows[number]>[]}
            data={rows}
            rowKey={r => r.id}
            searchKeys={["poNumber"]}
            onRowClick={r => onNavigate(`po-detail:${r.id}`)}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function GRNTab({ onNavigate }: { onNavigate: (pageId: string) => void }) {
  const erp = useERP();
  const { toast } = useToast();
  const rows = erp.goodsReceipts;
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total GRNs" value={formatNum(rows.length)} />
        <StatCard label="Accepted" value={formatNum(rows.filter(r => r.status === "Accepted").length)} tone="text-primary" />
        <StatCard label="Partial" value={formatNum(rows.filter(r => r.status === "Partial").length)} tone="text-destructive" />
        <StatCard label="In Inspection" value={formatNum(rows.filter(r => r.status === "Inspection").length)} />
      </div>
      <Card>
        <CardHeader><CardTitle>Goods Receipt Notes</CardTitle></CardHeader>
        <CardContent>
          <DataTable
            columns={[
              { key: "grnNumber", header: "GRN #", sortable: true, render: r => <span className="font-mono text-xs text-primary">{r.grnNumber}</span> },
              { key: "po", header: "PO #", render: r => erp.purchaseOrders.find(p => p.id === r.poId)?.poNumber ?? "—" },
              { key: "warehouse", header: "Warehouse", render: r => erp.getWarehouse(r.warehouseId)?.name ?? "—" },
              { key: "receivedDate", header: "Received Date", sortable: true },
              { key: "receivedBy", header: "Received By" },
              { key: "status", header: "Status", render: r => <Badge variant={r.status === "Accepted" ? "default" : r.status === "Rejected" ? "destructive" : "secondary"}>{r.status}</Badge> },
              { key: "actions", header: "Actions", align: "right", render: r => (
                <div className="flex justify-end gap-1" onClick={e => e.stopPropagation()}>
                  <Button size="sm" variant="ghost" className="size-7 p-0" onClick={() => onNavigate(`grn-detail:${r.id}`)}><Eye className="size-3.5" /></Button>
                  {r.status === "Inspection" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-600" onClick={() => { erp.postGRN(r.id, r.receivedBy); toast({ title: "GRN Posted", description: `${r.grnNumber} posted. Stock updated. JE created. PO status synced.` }); }}><Send className="size-3 mr-1" />Post GRN</Button>
                  )}
                </div>
              )},
            ] as Column<typeof rows[number]>[]}
            data={rows}
            rowKey={r => r.id}
            searchKeys={["grnNumber"]}
            onRowClick={r => onNavigate(`grn-detail:${r.id}`)}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function SupplierInvoiceTab() {
  const erp = useERP();
  const { toast } = useToast();
  const rows = erp.purchaseOrders.filter(p => p.status === "Received" || p.status === "Partially Received").map(p => {
    const grn = erp.goodsReceipts.find(g => g.poId === p.id);
    return {
      id: p.id, siNumber: grn ? `SI-${grn.grnNumber.split("-").pop()}` : "—",
      poNumber: p.poNumber, supplier: erp.getSupplier(p.supplierId)?.name ?? "—",
      date: grn?.receivedDate ?? p.orderDate, amount: p.total, status: p.status === "Received" ? "Posted" : "Pending",
    };
  });
  const handleCreate = (r: typeof rows[number]) => {
    // C4 fix: use returned JE id, not stale state
    const jeId = erp.createJE({
      date: new Date().toISOString().slice(0, 10),
      description: `Supplier invoice ${r.siNumber} from ${r.supplier}`,
      lines: [
        { account: "Inventory", debit: r.amount, credit: 0 },
        { account: "Accounts Payable", debit: 0, credit: r.amount },
      ],
    });
    erp.postJE(jeId, "Rumana Begum");
    toast({ title: "Supplier Invoice Created", description: `${r.siNumber} created for ${r.supplier} — ${formatBDT(r.amount)}. AP increased.` });
  };
  return (
    <Card>
      <CardHeader><CardTitle>Supplier Invoices</CardTitle></CardHeader>
      <CardContent>
        <DataTable columns={[
          { key: "siNumber", header: "SI #", render: r => <span className="font-mono text-xs">{r.siNumber}</span> },
          { key: "poNumber", header: "PO #" },
          { key: "supplier", header: "Supplier", sortable: true },
          { key: "date", header: "Date" },
          { key: "amount", header: "Amount", align: "right", sortable: true, render: r => formatBDT(r.amount) },
          { key: "status", header: "Status", render: r => <Badge variant={r.status === "Posted" ? "default" : "secondary"}>{r.status}</Badge> },
          { key: "action", header: "Action", align: "right", render: r => r.status === "Pending" ? (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleCreate(r)}><FileText className="size-3.5 mr-1" /> Create Invoice</Button>
          ) : <span className="text-xs text-muted-foreground">—</span> },
        ] as Column<typeof rows[number]>[]} data={rows} rowKey={r => r.id} searchKeys={["siNumber", "poNumber", "supplier"]} />
      </CardContent>
    </Card>
  );
}

function PaymentsTab() {
  const erp = useERP();
  const { toast } = useToast();
  const [approvedIds, setApprovedIds] = React.useState<Set<string>>(new Set());
  const [releasedIds, setReleasedIds] = React.useState<Set<string>>(new Set());
  const rows = erp.purchaseOrders.filter(p => p.status === "Received").map(p => ({
    id: p.id, payNumber: `PAY-${p.poNumber.split("-").pop()}`, poNumber: p.poNumber,
    supplier: erp.getSupplier(p.supplierId)?.name ?? "—",
    amount: p.total, mode: "Bank Transfer",
  }));
  const getStatus = (id: string) => releasedIds.has(id) ? "Released" : approvedIds.has(id) ? "Approved" : "Pending Approval";
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard label="Pending Payment" value={formatBDT(rows.filter(r => !approvedIds.has(r.id)).reduce((s, r) => s + r.amount, 0))} tone="text-destructive" />
        <StatCard label="Approved (pending release)" value={formatBDT(rows.filter(r => approvedIds.has(r.id) && !releasedIds.has(r.id)).reduce((s, r) => s + r.amount, 0))} tone="text-amber-600" />
        <StatCard label="Released" value={formatBDT(rows.filter(r => releasedIds.has(r.id)).reduce((s, r) => s + r.amount, 0))} tone="text-primary" />
      </div>
      <Card>
        <CardHeader><CardTitle>Payment Queue</CardTitle></CardHeader>
        <CardContent>
          <DataTable columns={[
            { key: "payNumber", header: "Pay #", render: r => <span className="font-mono text-xs">{r.payNumber}</span> },
            { key: "poNumber", header: "PO #" },
            { key: "supplier", header: "Supplier", sortable: true },
            { key: "amount", header: "Amount", align: "right", sortable: true, render: r => formatBDT(r.amount) },
            { key: "mode", header: "Mode" },
            { key: "status", header: "Status", render: r => {
              const s = getStatus(r.id);
              return <Badge variant={s === "Released" ? "default" : s === "Approved" ? "secondary" : "outline"}>{s}</Badge>;
            } },
            { key: "actions", header: "Actions", align: "right", render: r => {
              const s = getStatus(r.id);
              return (
                <div className="flex justify-end gap-1">
                  {s === "Pending Approval" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-600" onClick={() => { setApprovedIds(prev => new Set([...prev, r.id])); toast({ title: "Payment Approved", description: `${r.payNumber} for ${r.supplier} approved.` }); }}><CheckCircle2 className="size-3 mr-1" />Approve</Button>
                  )}
                  {s === "Approved" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs text-primary" onClick={() => {
                      setReleasedIds(prev => new Set([...prev, r.id]));
                      // C4 fix: use returned JE id, post directly
                      const jeId = erp.createJE({
                        date: new Date().toISOString().slice(0, 10),
                        description: `Supplier payment released for ${r.supplier} (${r.payNumber})`,
                        lines: [
                          { account: "Accounts Payable", debit: r.amount, credit: 0 },
                          { account: "Bank Account", debit: 0, credit: r.amount },
                        ],
                      });
                      erp.postJE(jeId, "Rumana Begum");
                      toast({ title: "Payment Released", description: `${formatBDT(r.amount)} released to ${r.supplier}. JE posted.` });
                    }}><Wallet className="size-3 mr-1" />Release</Button>
                  )}
                  {s === "Released" && <span className="text-xs text-muted-foreground">Completed</span>}
                </div>
              );
            } },
          ] as Column<typeof rows[number]>[]} data={rows} rowKey={r => r.id} searchKeys={["payNumber", "poNumber", "supplier"]} />
        </CardContent>
      </Card>
    </div>
  );
}

function SuppliersTab({ onNavigate }: { onNavigate: (pageId: string) => void }) {
  const erp = useERP();
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between"><CardTitle>Suppliers</CardTitle>
        <Button size="sm" onClick={() => onNavigate("md-suppliers-new")}><Plus className="size-4" /> New Supplier</Button>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={[
            { key: "code", header: "Code", sortable: true, render: r => <span className="font-mono text-xs">{r.code}</span> },
            { key: "name", header: "Supplier", sortable: true },
            { key: "category", header: "Category" },
            { key: "leadTimeDays", header: "Lead Time", align: "right", render: r => `${r.leadTimeDays}d` },
            { key: "rating", header: "Rating", align: "right", render: r => `★ ${r.rating.toFixed(1)}` },
            { key: "paymentTerms", header: "Terms" },
            { key: "status", header: "Status", render: r => <Badge variant={r.status === "Active" ? "default" : "secondary"}>{r.status}</Badge> },
          ] as Column<typeof erp.suppliers[number]>[]}
          data={erp.suppliers}
          rowKey={r => r.id}
          searchKeys={["code", "name", "category"]}
        />
      </CardContent>
    </Card>
  );
}

function PTPGuide() {
  const steps = [
    { n: 1, title: "Raise Requisition", desc: "Department raises a purchase requisition with required items and quantities." },
    { n: 2, title: "Create PO", desc: "Procurement converts PR to PO and submits for approval." },
    { n: 3, title: "Approve / Reject", desc: "Manager reviews and approves PO based on budget and supplier rating." },
    { n: 4, title: "Goods Receipt (GRN)", desc: "Warehouse receives goods, inspects, and posts GRN with batch/expiry details." },
    { n: 5, title: "3-Way Match", desc: "System auto-matches PO ↔ GRN ↔ Supplier Invoice before payment." },
    { n: 6, title: "Create Payment", desc: "AP team creates payment voucher against matched supplier invoice." },
    { n: 7, title: "Approve & Release", desc: "Finance manager approves and releases payment to supplier bank account." },
  ];
  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><BookOpen className="size-5" /> Procure-to-Pay Workflow</CardTitle></CardHeader>
      <CardContent>
        <ol className="flex flex-col gap-4">
          {steps.map(s => (
            <li key={s.n} className="flex gap-4">
              <div className="bg-primary text-primary-foreground flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold">{s.n}</div>
              <div>
                <p className="font-semibold">{s.title}</p>
                <p className="text-muted-foreground text-sm">{s.desc}</p>
              </div>
              {s.n < steps.length && <div className="text-muted-foreground mt-9 hidden sm:block"><ArrowRight className="size-4" /></div>}
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
